/** Shared between the Worker and the browser. Keep it dependency-free. */

export const MAX_PASTE_BYTES = 512 * 1024
export const MAX_TITLE_LENGTH = 120

/** Unread "one serving" pastes self-destruct after this long, so secrets don't linger. */
export const BURN_SAFETY_TTL_SECONDS = 30 * 24 * 60 * 60

export const EXPIRY_OPTIONS = [
  { id: 'al-dente', label: 'al dente', hint: '1 hour', seconds: 60 * 60 },
  { id: 'served', label: 'served', hint: '1 day', seconds: 24 * 60 * 60 },
  { id: 'leftovers', label: 'leftovers', hint: '1 week', seconds: 7 * 24 * 60 * 60 },
  { id: 'preserved', label: 'preserved', hint: 'no expiry', seconds: null },
] as const

export type ExpiryId = (typeof EXPIRY_OPTIONS)[number]['id']

/** Saved notes and scripts shouldn't quietly evaporate, so keeping is the default. */
export const DEFAULT_EXPIRY: ExpiryId = 'preserved'

export function expirySeconds(id: string): number | null | undefined {
  const match = EXPIRY_OPTIONS.find((option) => option.id === id)
  return match ? match.seconds : undefined
}

export const VISIBILITIES = [
  { id: 'private', label: 'private', hint: 'only you' },
  { id: 'public', label: 'public', hint: 'anyone with the link' },
] as const

export type Visibility = (typeof VISIBILITIES)[number]['id']
export const DEFAULT_VISIBILITY: Visibility = 'private'

export function isVisibility(value: unknown): value is Visibility {
  return VISIBILITIES.some((entry) => entry.id === value)
}

/** Language ids the editor knows how to highlight. Also the server-side allowlist. */
export const LANGUAGE_IDS = [
  'text',
  'shell',
  'powershell',
  'yaml',
  'json',
  'dockerfile',
  'nginx',
  'terraform',
  'toml',
  'properties',
  'javascript',
  'typescript',
  'python',
  'go',
  'rust',
  'sql',
  'markdown',
  'html',
  'css',
  'xml',
  'diff',
] as const

export type LanguageId = (typeof LANGUAGE_IDS)[number]
export const DEFAULT_LANGUAGE: LanguageId = 'text'

export function isLanguageId(value: unknown): value is LanguageId {
  return typeof value === 'string' && (LANGUAGE_IDS as readonly string[]).includes(value)
}

export interface SessionUser {
  /** Display name for the single account. Passkeys carry no email address. */
  label: string
}

export interface AuthStateResponse {
  /** True once at least one passkey exists — before that, /setup is the only way in. */
  registered: boolean
  signedIn: boolean
  label: string | null
  /** False when PASSKEY_BOOTSTRAP_CODE was never set, so setup cannot proceed. */
  bootstrapReady: boolean
  /** False when the D1 migrations haven't been applied to this deployment. */
  schemaReady: boolean
}

export interface PasskeySummary {
  id: string
  name: string
  deviceType: string | null
  /** True for a synced credential — one 1Password or iCloud keeps a copy of. */
  backedUp: boolean
  createdAt: number
  lastUsedAt: number | null
}

export interface ListPasskeysResponse {
  passkeys: PasskeySummary[]
}

export interface MeResponse {
  user: SessionUser
}

/**
 * Delimiters wrapping each matched term inside a search snippet. Control
 * characters rather than markup: the snippet is a slice of someone's pasta, so
 * it reaches the DOM as text either way, and these can't collide with content.
 */
export const SNIPPET_OPEN = '\u0001'
export const SNIPPET_CLOSE = '\u0002'

/** Listing row — deliberately excludes `content`. */
export interface PasteSummary {
  id: string
  title: string | null
  language: LanguageId
  visibility: Visibility
  burnAfterRead: boolean
  sizeBytes: number
  createdAt: number
  updatedAt: number
  expiresAt: number | null
  /**
   * Match context, terms wrapped in the delimiters above. Only present on rows
   * that came back from a `?q=` search, and null when the hit was title-only.
   */
  snippet?: string | null
}

export interface ListPastesResponse {
  pastes: PasteSummary[]
}

export interface PasteDetail extends PasteSummary {
  content: string
  /** True when the requester owns this paste, so the UI can offer edit/delete. */
  owned: boolean
  /** True when this read consumed a one-serving paste — it no longer exists. */
  burned: boolean
}

export interface WritePasteRequest {
  content: string
  title?: string | null
  language?: string
  expiry?: string
  visibility?: string
  burnAfterRead?: boolean
}

export interface WritePasteResponse {
  id: string
  url: string
  rawUrl: string
  visibility: Visibility
  expiresAt: number | null
  burnAfterRead: boolean
}

/**
 * How many edits back a pasta keeps. Enforced by the trigger in
 * migrations/0004_versions.sql; repeated here so the UI can say so.
 */
export const MAX_VERSIONS = 20

/** One superseded revision of a pasta. Excludes `content`, like PasteSummary. */
export interface VersionSummary {
  id: number
  title: string | null
  language: LanguageId
  sizeBytes: number
  /** When this text stopped being current — the edit that replaced it. */
  replacedAt: number
}

export interface ListVersionsResponse {
  versions: VersionSummary[]
}

export interface VersionDetail extends VersionSummary {
  content: string
}

export interface TokenSummary {
  id: string
  name: string
  preview: string
  createdAt: number
  lastUsedAt: number | null
}

export interface ListTokensResponse {
  tokens: TokenSummary[]
}

export interface CreateTokenResponse {
  token: TokenSummary
  /** Shown once, never recoverable — only its hash is stored. */
  secret: string
}

export interface ApiErrorResponse {
  error: string
}
